import React from "react";
import Head from "next/head";
import parse from "html-react-parser";

import { useImage, useOtherFields, useWine } from "@/api/hooks";
import { UrlString } from "@/models/api";
import { Heading, Blockquote } from "@/components/Typography";
import { TastesLike } from "@/components/WineDetail/TastesLike/TastesLike";
import { DrinkWith } from "@/components/WineDetail/DrinkWith/DrinkWith";
import { BackgroundInfo } from "@/components/WineDetail/BackgroundInfo/BackgroundInfo";
import Navigation from "@/components/Navigation/Navigation";
import { Intro } from "@/components/WineDetail/Intro/Intro";
import { useRouter } from "next/router";

import s from "./wine-detail.module.scss";

export interface WineDetailProps {
	slug: string;
	domain: string | undefined;
	url: UrlString | undefined;
}

export default function WineDetailTemplate({
	slug,
	domain,
	url,
}: WineDetailProps): JSX.Element {
	const [wine] = useWine(slug);
	const [featuredImage] = useImage(wine?.featured_media);
	const leaderDescription = "";
	const metaImg = featuredImage
		? featuredImage?.media_details.sizes.medium.source_url
		: "";
	const [otherFields] = useOtherFields();

	const router = useRouter();
	const leftNaviation = router.query.seed ? "wine-overview" : "logo";

	if (wine === undefined) return <></>;

	const yoastHead = parse(wine.yoast_head!);

	return (
		<>
			<Head>
				{yoastHead}
				<meta property="og:image" content={metaImg} />
				{/* Twitter Meta Tags */}
				<meta name="twitter:card" content="summary_large_image" />
				<meta property="twitter:domain" content={domain} />
				<meta property="twitter:url" content={url} />
				<meta name="twitter:title" content={wine.name} />
				<meta name="twitter:description" content={leaderDescription} />
				<meta name="twitter:image" content={metaImg} />
			</Head>
			<Navigation left={leftNaviation} right="menu" />

			{wine && (
				<main
					className={s.winedetail}
					itemScope
					itemType="https://schema.org/Product"
				>
					<Intro wine={wine} />

					<TastesLike
						data={wine.acf.tastes_like}
						title={otherFields?.acf.taste_like_title}
					/>

					<DrinkWith
						data={wine.acf.drink_with}
						title={otherFields?.acf.drink_with_title}
					/>

					<section className={s.quoteSection}>
						<Blockquote>{wine.acf.quote}</Blockquote>
						<div className={s.quotee}>
							<Heading
								as="h6"
								sizeMobile="xxs"
								sizeDesktop="xxs"
								className={s.quotee__name}
							>
								{wine.acf.quotee.name}
							</Heading>
							<Heading
								as="h6"
								sizeMobile="xxs"
								sizeDesktop="xxs"
								className={s.quotee__work}
							>
								{wine.acf.quotee.credentials}
							</Heading>
						</div>
					</section>

					<BackgroundInfo
						content={wine.acf.background_info}
						title={otherFields?.acf.background_info_title}
						tasteProfile={wine.acf.taste_profile.name}
					/>
				</main>
			)}
		</>
	);
}
